import React from 'react';
import {motion} from 'framer-motion';
import {Link} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import {useAuth0Data} from '../../../context/Auth0DataContext';

const {FiUsers,FiTrendingUp,FiTrendingDown,FiPlus,FiAward,FiTarget,FiExternalLink} = FiIcons;

const SalesTeamGMWidget = () => {
  const {data, salesTeamYear, salesTeamAvailableYears, setSalesTeamYear} = useAuth0Data();

  // Filter sales team members who are NOT client coordinators (only show GM for sales roles)
  const salesTeamMembers = data.salesTeam.filter(member =>
    member.role !== 'client-coordinator'
  );

  // Generate year options
  const currentYear = new Date().getFullYear();
  const yearOptions = [...new Set([...salesTeamAvailableYears, currentYear])].sort((a, b) => b - a);

  const handleYearChange = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSalesTeamYear(parseInt(e.target.value));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US',{
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (salesTeamMembers.length === 0) {
    return (
      <Link to="/data-management/sales-team" className="block">
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          className="relative overflow-hidden glass rounded-2xl border border-white/20 shadow-xl hover:shadow-2xl transition-all duration-300 cursor-pointer group"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-teal-500/5 to-cyan-500/5"></div>
          
          {/* Link indicator */}
          <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <SafeIcon icon={FiExternalLink} className="w-4 h-4 text-emerald-600" />
          </div>

          <div className="relative p-6">
            <div className="flex items-center mb-6">
              <motion.div
                className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg"
                whileHover={{rotate: 360,scale: 1.1}}
                transition={{duration: 0.6}}
              >
                <SafeIcon icon={FiUsers} className="w-6 h-6 text-white" />
              </motion.div>
              <div className="ml-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-xl font-bold text-gray-900">Sales Team GM Progress</h3>
                  <select
                    value={salesTeamYear}
                    onChange={handleYearChange}
                    onClick={(e) => e.preventDefault()}
                    className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-medium bg-white cursor-pointer"
                  >
                    {yearOptions.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <p className="text-gray-500">Track team performance</p>
              </div>
            </div>

            <div className="text-center py-16">
              <motion.div
                initial={{scale: 0}}
                animate={{scale: 1}}
                transition={{delay: 0.2,type: "spring"}}
                className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mx-auto mb-6"
              >
                <SafeIcon icon={FiPlus} className="w-12 h-12 text-gray-400" />
              </motion.div>
              <h4 className="text-lg font-semibold text-gray-700 mb-2">No Sales Team Members</h4>
              <p className="text-gray-500 mb-6">Click to add sales team members and track performance</p>
              <motion.div
                whileHover={{scale: 1.05}}
                whileTap={{scale: 0.95}}
                className="btn-primary px-6 py-3 rounded-xl inline-flex items-center"
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
                Add Team Members
              </motion.div>
            </div>
          </div>
        </motion.div>
      </Link>
    );
  }

  const teamTotal = salesTeamMembers.reduce((sum,member) => sum + (member.gmCurrent || 0),0);
  const teamGoal = salesTeamMembers.reduce((sum,member) => sum + (member.gmGoal || 0),0);
  const teamProgress = teamGoal > 0 ? (teamTotal / teamGoal) * 100 : 0;

  return (
    <Link to="/data-management/sales-team" className="block">
      <motion.div
        initial={{opacity: 0,y: 20}}
        animate={{opacity: 1,y: 0}}
        className="relative overflow-hidden glass rounded-2xl border border-white/20 shadow-xl hover:shadow-2xl transition-all duration-300 cursor-pointer group"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-teal-500/5 to-cyan-500/5"></div>
        <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-emerald-400/20 to-teal-400/20 rounded-full blur-2xl"></div>
        
        {/* Link indicator */}
        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <SafeIcon icon={FiExternalLink} className="w-4 h-4 text-emerald-600" />
        </div>

        <div className="relative p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <motion.div
                className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg"
                whileHover={{rotate: 360,scale: 1.1}}
                transition={{duration: 0.6}}
              >
                <SafeIcon icon={FiUsers} className="w-6 h-6 text-white" />
              </motion.div>
              <div className="ml-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-xl font-bold text-gray-900">Sales Team GM Progress</h3>
                  <select
                    value={salesTeamYear}
                    onChange={handleYearChange}
                    onClick={(e) => e.preventDefault()}
                    className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-medium bg-white cursor-pointer"
                  >
                    {yearOptions.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <p className="text-gray-500">{salesTeamMembers.length} team members</p>
              </div>
            </div>
            <motion.div
              className="text-right"
              initial={{opacity: 0,x: 20}}
              animate={{opacity: 1,x: 0}}
              transition={{delay: 0.2}}
            >
              <div className="flex items-center text-emerald-600">
                <SafeIcon icon={FiAward} className="w-5 h-5 mr-2" />
                <span className="text-lg font-bold">{teamProgress.toFixed(1)}%</span>
              </div>
              <p className="text-xs text-gray-500">Team Progress</p>
            </motion.div>
          </div>

          {/* Team Members */}
          <div className="space-y-4 mb-6">
            {salesTeamMembers.map((member,index) => {
              const percentage = (member.gmGoal || 0) > 0 ? ((member.gmCurrent || 0) / (member.gmGoal || 0)) * 100 : 0;
              const isOnTrack = percentage >= 75;

              return (
                <motion.div
                  key={member.id}
                  initial={{opacity: 0,x: -20}}
                  animate={{opacity: 1,x: 0}}
                  transition={{delay: index * 0.1}}
                  className="bg-white/60 backdrop-blur-sm border border-white/20 rounded-xl p-4 hover:bg-white/80 transition-all duration-200"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-lg flex items-center justify-center text-white font-semibold text-sm">
                        {member.name.charAt(0)}
                      </div>
                      <div className="ml-3">
                        <h4 className="font-semibold text-gray-900">{member.name}</h4>
                        <div className="flex items-center">
                          <p className="text-sm text-gray-500 mr-2">
                            {formatCurrency(member.gmCurrent || 0)} / {formatCurrency(member.gmGoal || 0)}
                          </p>
                          {member.role === 'business-development-director' && (
                            <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full">
                              BD Director
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className={`flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      isOnTrack ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'
                    }`}>
                      <SafeIcon icon={isOnTrack ? FiTrendingUp : FiTrendingDown} className="w-4 h-4 mr-1" />
                      {percentage.toFixed(1)}%
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="relative">
                    <div className="w-full bg-gray-200 rounded-full h-2 shadow-inner">
                      <motion.div
                        initial={{width: 0}}
                        animate={{width: `${Math.min(percentage,100)}%`}}
                        transition={{duration: 1,ease: "easeOut",delay: 0.5 + index * 0.1}}
                        className={`h-2 rounded-full ${
                          isOnTrack ? 'bg-gradient-to-r from-emerald-500 to-teal-500' : 'bg-gradient-to-r from-orange-500 to-red-500'
                        }`}
                      />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Team Summary */}
          <motion.div
            className="pt-6 border-t border-white/20"
            initial={{opacity: 0,y: 20}}
            animate={{opacity: 1,y: 0}}
            transition={{delay: 0.6}}
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl">
                <SafeIcon icon={FiTarget} className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                <p className="text-sm text-gray-600 mb-1">Team Total GM</p>
                <p className="text-xl font-bold text-emerald-600">{formatCurrency(teamTotal)}</p>
              </div>
              <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl">
                <SafeIcon icon={FiAward} className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                <p className="text-sm text-gray-600 mb-1">Team Goal</p>
                <p className="text-xl font-bold text-blue-600">{formatCurrency(teamGoal)}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </Link>
  );
};

export default SalesTeamGMWidget;